import {
    createContext,
    CSSProperties,
    HTMLAttributes,
    Key,
    ReactNode,
    Ref,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { Button, Checkbox, Col, Flex, Row } from 'antd';
import { TbChevronDown, TbInbox } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useDismissibleSelection } from '@/hooks/useDismissibleSelection';

import EmptySection from '../../layouts/views/EmptySection';
import SmallTag from '../../tags/SmallTag';
import VirtualizedList from '../VirtualizedList';

interface CompactVirtualizedListProps<T> extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'title'> {
    actions?: ReactNode;
    animated?: boolean;
    bodyFooter?: ReactNode;
    bodyProps?: object;
    bodyRef?: Ref<HTMLDivElement>;
    bodyStyle?: CSSProperties;
    collapsed?: boolean;
    collapsible?: boolean;
    count?: number;
    dataSource?: T[];
    defaultCollapsed?: boolean;
    dismissSelectionOnOutside?: boolean;
    emptyDescription?: ReactNode;
    emptyExtra?: ReactNode;
    emptyHeight?: number | string;
    emptyIcon?: ReactNode;
    emptyLabel?: ReactNode;
    emptyState?: ReactNode;
    estimateSize?: number;
    getItemKey?: (item: T, index: number) => string | number;
    hasMore?: boolean;
    headerClassName?: string;
    headerGap?: number | string;
    headerPrefix?: ReactNode;
    headerPrefixHoverOnly?: boolean;
    headerProps?: HTMLAttributes<HTMLDivElement>;
    headerStyle?: CSSProperties;
    icon?: ReactNode;
    isItemSelectable?: (item: T, index: number) => boolean;
    listHeight?: string | number;
    listStyle?: CSSProperties;
    loading?: boolean;
    loadingItemCount?: number;
    loadingMore?: boolean;
    onCollapsedChange?: (collapsed: boolean) => void;
    onEndReached?: () => void;
    onSelectedKeysChange?: (selectedKeys: ReadonlySet<Key>) => void;
    renderItem?: (item: T, index: number) => ReactNode;
    renderLoadingItem?: (index: number) => ReactNode;
    rowKey?: ((item: T) => Key) | keyof T;
    selectable?: boolean;
    selectionLabel?: string;
    selectedKeys?: ReadonlySet<Key>;
    showHeader?: boolean;
    showCount?: boolean;
    stickyHeader?: boolean;
    title: ReactNode;
}

interface CompactVirtualizedListItemSelectionContextValue {
    selected: boolean;
    selectable: boolean;
    selectionKey?: Key;
    toggleSelected: () => void;
}

const CompactVirtualizedListItemSelectionContext = createContext<CompactVirtualizedListItemSelectionContextValue>({
    selected: false,
    selectable: false,
    toggleSelected: () => undefined,
});

interface CompactVirtualizedListItemProps extends HTMLAttributes<HTMLDivElement> {
    backgroundColor?: string;
    children: ReactNode;
    contentGap?: CSSProperties['columnGap'];
    hoverBackgroundColor?: string;
    hovered?: boolean;
    hoverable?: boolean;
    hoverLeading?: ReactNode;
    leading?: ReactNode;
    onHoveredChange?: (hovered: boolean) => void;
}

type CompactVirtualizedListComponent = (<T>(props: CompactVirtualizedListProps<T>) => JSX.Element) & {
    Item: (props: CompactVirtualizedListItemProps) => JSX.Element;
};

const CompactVirtualizedList = (<T,>({
    actions,
    animated = true,
    bodyFooter,
    bodyProps,
    bodyRef,
    bodyStyle,
    className,
    collapsed,
    collapsible = true,
    count,
    dataSource = [],
    defaultCollapsed = false,
    dismissSelectionOnOutside = true,
    emptyDescription = 'Add resources to this section to keep related work organized.',
    emptyExtra,
    emptyHeight = 120,
    emptyIcon,
    emptyLabel = 'No Items',
    emptyState,
    estimateSize = 40,
    getItemKey,
    hasMore,
    headerClassName,
    headerGap = 'small',
    headerPrefix,
    headerPrefixHoverOnly = false,
    headerProps,
    headerStyle,
    icon,
    isItemSelectable,
    listHeight,
    listStyle,
    loading = false,
    loadingItemCount,
    loadingMore = false,
    onCollapsedChange,
    onEndReached,
    onSelectedKeysChange,
    renderItem,
    renderLoadingItem,
    rowKey,
    selectable = false,
    selectionLabel,
    selectedKeys = new Set<Key>(),
    showHeader = true,
    showCount = true,
    stickyHeader = true,
    style,
    title,
    ...props
}: CompactVirtualizedListProps<T>): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
    const [headerHovered, setHeaderHovered] = useState(false);
    const [animationParent] = useAutoAnimate({
        duration: 175,
        easing: 'ease-in',
    });
    const resolvedCollapsed = collapsed ?? internalCollapsed;
    const resolvedCount = count ?? dataSource.length;
    const showCountTag = showCount && resolvedCount > 0;
    const showHeaderPrefix = !headerPrefixHoverOnly || headerHovered;
    const animationRef = animated ? animationParent : undefined;
    const itemKeys = useMemo(
        () =>
            dataSource.map((item, index) => {
                if (getItemKey) {
                    return getItemKey(item, index);
                }

                if (typeof rowKey === 'function') {
                    return rowKey(item);
                }

                if (rowKey) {
                    return item[rowKey] as Key;
                }

                return index;
            }),
        [dataSource, getItemKey, rowKey],
    );
    const selectableItemKeys = useMemo(
        () => itemKeys.filter((_, index) => !isItemSelectable || isItemSelectable(dataSource[index], index)),
        [dataSource, isItemSelectable, itemKeys],
    );
    const selectedSelectableItemCount = selectableItemKeys.filter((key) => selectedKeys.has(key)).length;
    const allItemsSelected = selectableItemKeys.length > 0 && selectedSelectableItemCount === selectableItemKeys.length;
    const someItemsSelected = selectedSelectableItemCount > 0 && !allItemsSelected;
    const showHeaderSelection = selectable && (headerHovered || selectedSelectableItemCount > 0);

    useEffect(() => {
        if (collapsed === undefined) {
            setInternalCollapsed(defaultCollapsed);
        }
    }, [collapsed, defaultCollapsed]);

    const clearSelection = useCallback(() => onSelectedKeysChange?.(new Set<Key>()), [onSelectedKeysChange]);

    useDismissibleSelection({
        active: dismissSelectionOnOutside && selectable && selectedKeys.size > 0 && Boolean(onSelectedKeysChange),
        onDismiss: clearSelection,
    });

    const handleCollapse = () => {
        const nextCollapsed = !resolvedCollapsed;

        if (collapsed === undefined) {
            setInternalCollapsed(nextCollapsed);
        }

        onCollapsedChange?.(nextCollapsed);
    };

    const handleGroupSelectionChange = () => {
        const nextSelectedKeys = new Set(selectedKeys);

        if (allItemsSelected) {
            selectableItemKeys.forEach((key) => nextSelectedKeys.delete(key));
        } else {
            selectableItemKeys.forEach((key) => nextSelectedKeys.add(key));
        }

        onSelectedKeysChange?.(nextSelectedKeys);
    };

    const resolvedEmptyState = useMemo(() => {
        if (emptyState) {
            return emptyState;
        }

        return (
            <EmptySection
                height={emptyHeight}
                icon={emptyIcon ?? <TbInbox size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                label={emptyLabel}
                description={emptyDescription}
                extra={emptyExtra}
            />
        );
    }, [emptyDescription, emptyExtra, emptyHeight, emptyIcon, emptyLabel, emptyState, token.colorTextTertiary]);

    return (
        <div ref={animationRef} className={className} style={style} {...props}>
            {showHeader && (
                <Row
                    align='middle'
                    className={headerClassName}
                    {...headerProps}
                    style={{
                        backgroundColor: token.colorBgBase,
                        borderBottom: `1px solid ${token.colorBorder}`,
                        padding: '0.25rem 1.5rem',
                        position: stickyHeader ? 'sticky' : undefined,
                        top: stickyHeader ? 0 : undefined,
                        ...headerProps?.style,
                        ...headerStyle,
                    }}
                    onMouseEnter={(event) => {
                        setHeaderHovered(true);
                        headerProps?.onMouseEnter?.(event);
                    }}
                    onMouseLeave={(event) => {
                        setHeaderHovered(false);
                        headerProps?.onMouseLeave?.(event);
                    }}
                >
                    <Col flex='auto' style={{ minWidth: 0 }}>
                        <Flex
                            align='center'
                            gap={showHeaderSelection ? headerGap : 0}
                            style={{ minWidth: 0, transition: 'gap 150ms ease' }}
                        >
                            {selectable && (
                                <span
                                    data-compact-list-selection-control='true'
                                    style={{
                                        display: 'inline-flex',
                                        flex: '0 0 auto',
                                        opacity: showHeaderSelection ? 1 : 0,
                                        overflow: 'hidden',
                                        pointerEvents: showHeaderSelection ? 'auto' : 'none',
                                        transition: 'opacity 150ms ease, width 150ms ease',
                                        width: showHeaderSelection ? '1rem' : 0,
                                    }}
                                >
                                    <Checkbox
                                        aria-label={`${allItemsSelected ? 'Deselect' : 'Select'} all ${selectionLabel ?? (typeof title === 'string' ? title : 'items')}`}
                                        checked={allItemsSelected}
                                        indeterminate={someItemsSelected}
                                        onChange={handleGroupSelectionChange}
                                    />
                                </span>
                            )}
                            <Flex align='center' gap={headerGap} style={{ minWidth: 0 }}>
                                {collapsible && (
                                    <Button size='small' type='text' onClick={handleCollapse}>
                                        <TbChevronDown
                                            style={{
                                                color: token.colorTextTertiary,
                                                transform: resolvedCollapsed ? 'rotate(-90deg)' : undefined,
                                                transition: 'transform 250ms ease',
                                            }}
                                        />
                                    </Button>
                                )}
                                <Flex
                                    align='center'
                                    gap={headerPrefix && showHeaderPrefix ? headerGap : 0}
                                    style={{ minWidth: 0, transition: 'gap 150ms ease' }}
                                >
                                    {headerPrefix && (
                                        <span
                                            style={{
                                                display: 'inline-flex',
                                                opacity: showHeaderPrefix ? 1 : 0,
                                                overflow: 'hidden',
                                                pointerEvents: showHeaderPrefix ? 'auto' : 'none',
                                                transition: 'opacity 150ms ease, width 150ms ease',
                                                width: showHeaderPrefix ? '1.5rem' : 0,
                                            }}
                                        >
                                            {headerPrefix}
                                        </span>
                                    )}
                                    <Flex
                                        align='center'
                                        gap={token.marginXXS}
                                        style={{ color: token.colorText, minWidth: 0 }}
                                    >
                                        {icon}
                                        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {title}
                                        </span>
                                    </Flex>
                                </Flex>
                                {showCountTag && (
                                    <SmallTag
                                        message={
                                            <span style={{ color: token.colorTextTertiary }}>{resolvedCount}</span>
                                        }
                                    />
                                )}
                            </Flex>
                        </Flex>
                    </Col>
                    {actions && <Col flex='none'>{actions}</Col>}
                </Row>
            )}
            {!resolvedCollapsed && (
                <div
                    ref={bodyRef}
                    {...bodyProps}
                    style={{
                        ...bodyStyle,
                    }}
                >
                    {loading || dataSource.length > 0 ? (
                        <VirtualizedList
                            dataSource={dataSource}
                            estimateSize={estimateSize}
                            getItemKey={getItemKey}
                            hasMore={hasMore}
                            height={listHeight}
                            loading={loading}
                            loadingItemCount={loadingItemCount}
                            loadingMore={loadingMore}
                            rowKey={rowKey}
                            size='small'
                            style={listStyle}
                            onEndReached={onEndReached}
                            renderLoadingItem={renderLoadingItem}
                            renderItem={(item, index) => {
                                const selectionKey = itemKeys[index];
                                const selected = selectedKeys.has(selectionKey);
                                const itemSelectable =
                                    selectable && (!isItemSelectable || isItemSelectable(item, index));

                                return (
                                    <CompactVirtualizedListItemSelectionContext.Provider
                                        value={{
                                            selectable: itemSelectable,
                                            selected,
                                            selectionKey,
                                            toggleSelected: () => {
                                                const nextSelectedKeys = new Set(selectedKeys);

                                                if (selected) {
                                                    nextSelectedKeys.delete(selectionKey);
                                                } else {
                                                    nextSelectedKeys.add(selectionKey);
                                                }

                                                onSelectedKeysChange?.(nextSelectedKeys);
                                            },
                                        }}
                                    >
                                        {renderItem?.(item, index)}
                                    </CompactVirtualizedListItemSelectionContext.Provider>
                                );
                            }}
                        />
                    ) : (
                        resolvedEmptyState
                    )}
                    {bodyFooter}
                </div>
            )}
        </div>
    );
}) as CompactVirtualizedListComponent;

const CompactVirtualizedListItem = ({
    backgroundColor,
    children,
    contentGap = '0.75rem',
    hoverBackgroundColor,
    hovered,
    hoverable = true,
    hoverLeading,
    leading,
    onHoveredChange,
    onMouseEnter,
    onMouseLeave,
    style,
    ...props
}: CompactVirtualizedListItemProps): JSX.Element => {
    const { token } = useAntDesignTheme();
    const selection = useContext(CompactVirtualizedListItemSelectionContext);
    const [internalHovered, setInternalHovered] = useState(false);
    const resolvedHovered = hovered ?? internalHovered;
    const resolvedBackgroundColor = backgroundColor ?? token.colorBgContainer;
    const resolvedHoverBackgroundColor = hoverBackgroundColor ?? token.colorBgBase;
    const showSelection = selection.selectable && (resolvedHovered || selection.selected);

    const handleHoveredChange = (nextHovered: boolean) => {
        if (hovered === undefined) {
            setInternalHovered(nextHovered);
        }

        onHoveredChange?.(nextHovered);
    };

    return (
        <Row
            align='middle'
            wrap={false}
            style={{
                backgroundColor: hoverable && resolvedHovered ? resolvedHoverBackgroundColor : resolvedBackgroundColor,
                borderBottom: `1px solid ${token.colorBorder}`,
                minWidth: 0,
                padding: '0.25rem 1.5rem',
                width: '100%',
                ...style,
            }}
            onMouseEnter={(event) => {
                handleHoveredChange(true);
                onMouseEnter?.(event);
            }}
            onMouseLeave={(event) => {
                handleHoveredChange(false);
                onMouseLeave?.(event);
            }}
            {...props}
        >
            {hoverLeading && (
                <span
                    style={{
                        alignItems: 'center',
                        display: 'inline-flex',
                        flex: `0 0 ${resolvedHovered ? '1.5rem' : 0}`,
                        justifyContent: 'center',
                        marginRight: resolvedHovered ? '0.25rem' : 0,
                        opacity: resolvedHovered ? 1 : 0,
                        overflow: 'hidden',
                        pointerEvents: resolvedHovered ? 'auto' : 'none',
                        transition:
                            'flex-basis 150ms ease, margin-right 150ms ease, opacity 150ms ease, width 150ms ease',
                        width: resolvedHovered ? '1.5rem' : 0,
                    }}
                >
                    {hoverLeading}
                </span>
            )}
            {selection.selectable && (
                <Col
                    data-compact-list-selection-control='true'
                    flex='none'
                    style={{
                        alignItems: 'center',
                        display: 'inline-flex',
                        opacity: showSelection ? 1 : 0,
                        overflow: 'hidden',
                        pointerEvents: showSelection ? 'auto' : 'none',
                        marginRight: showSelection ? '0.5rem' : 0,
                        transition: 'margin-right 150ms ease, opacity 150ms ease, width 150ms ease',
                        width: showSelection ? '1rem' : 0,
                    }}
                >
                    <Checkbox
                        aria-label={`${selection.selected ? 'Deselect' : 'Select'} item`}
                        checked={selection.selected}
                        onChange={selection.toggleSelected}
                        onClick={(event) => event.stopPropagation()}
                    />
                </Col>
            )}
            {leading && (
                <span
                    style={{
                        display: 'inline-flex',
                        flex: '0 0 auto',
                        marginRight: '0.5rem',
                    }}
                >
                    {leading}
                </span>
            )}
            <Row
                align='middle'
                wrap={false}
                style={{
                    columnGap: contentGap,
                    flex: '1 1 auto',
                    minWidth: 0,
                }}
            >
                {children}
            </Row>
        </Row>
    );
};

CompactVirtualizedList.Item = CompactVirtualizedListItem;

export default CompactVirtualizedList;
